import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { axiosClient } from '@/shared';
import { ApiResponse, ErrorResponse } from '@/shared/types';
import { handleTtsError } from '@/shared/lib/tts-blocked-error';

/**
 * TTS 미리듣기 요청.
 * - 서버는 S3 저장 없이 오디오 base64를 직접 반환한다.
 * - 실제 생성과 같은 합성 설정을 서버에 전달한다.
 *
 * voice/emotion은 BE 등록 코드(VoiceType.code / Emotion.code)를 그대로 전달한다.
 * enum 동기화 부담을 없애기 위해 string 스키마를 유지하되, 검증은 BE가 담당한다.
 */
export const PostTtsVoicePreviewRequestSchema = z.object({
  // voice 직접 지정은 기존 호출 하위 호환용이다. 새 fallback 경로는 useCase 만 보내고
  // BE TtsVoiceRuntimeResolver 가 실제 voice 를 선택한다.
  voice: z.string().min(1).optional(),
  useCase: z.string().min(1).optional(),
  text: z.string(),
  emotion: z.string().optional(),
  speakingRate: z.number().positive().optional(),
  pitch: z.number().nullable().optional(),
  /** 지원하는 TTS 공급자의 자연어 지시 (예: "천천히 슬프게"). */
  style: z.string().optional(),
  language: z.enum(['KOREAN', 'ENGLISH']).optional(),
  /**
   * 비용/사용량 집계용 호출 출처 식별자 (예: 'tts_maker'). 일반 preview 는 생략.
   * BE/AI 가 usage 기록의 source_detail 로 남겨 나중에 도구별 비용을 분리 집계한다.
   */
  sourceDetail: z.string().optional(),
  /** TTS Maker 프로젝트 id — usage 집계용(선택). */
  ttsMakerProjectId: z.number().optional(),
  /** TTS Maker 세그먼트 id — usage 집계용(선택). */
  ttsMakerSegmentId: z.string().optional(),
  /**
   * 반환 오디오 포맷. 'wav'(기본, 세션 재생용) | 'mp3'(영구 저장용).
   * TTS Maker 는 생성 시점부터 'mp3' 로 받아 저장 표준(MP3)과 일치시킨다. 일반 preview 는 생략 → wav.
   */
  outputFormat: z.enum(['wav', 'mp3']).optional(),
  /**
   * 여러 포맷을 한 번의 합성으로 받기(예: TTS Maker 가 mp3+wav 둘 다 보관).
   * 지정 시 outputFormat 대신 이 목록을 쓰며 응답 variants 로 각 포맷을 받는다.
   */
  outputFormats: z.array(z.enum(['wav', 'mp3'])).optional(),
});

export type PostTtsVoicePreviewRequestType = z.infer<
  typeof PostTtsVoicePreviewRequestSchema
>;

export type TtsVoicePreviewVariant = {
  mime: string;
  audioBase64: string;
  durationSec: number;
};

export type TtsVoicePreviewResponse = {
  audioBase64: string;
  durationSec: number;
  mime: string;
  /** outputFormats 요청 시 각 포맷 산출물(주 포맷 포함). 단일 요청이면 빈 배열/미포함. */
  variants?: TtsVoicePreviewVariant[];
};

type Response = ApiResponse<TtsVoicePreviewResponse>;

export const postTtsVoicePreviewApi = async (
  body: PostTtsVoicePreviewRequestType,
  signal?: AbortSignal,
) => {
  try {
    const response = await axiosClient.post<Response>(
      '/v1.0/tts/voice/preview',
      { ...body },
      { signal },
    );
    return response.data;
  } catch (error) {
    handleTtsError(error, 'voice-preview');
    return Promise.reject(error);
  }
};

export const usePostTtsVoicePreviewApi = () => {
  return useMutation<
    Response,
    AxiosError<ErrorResponse>,
    PostTtsVoicePreviewRequestType
  >({
    mutationFn: (body) => postTtsVoicePreviewApi(body),
  });
};

/** base64 → Blob URL 유틸. 사용 후 URL.revokeObjectURL(url) 권장. */
export function base64ToBlobUrl(
  base64: string,
  mime: string = 'audio/wav',
): string {
  const byteChars = atob(base64);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: mime }));
}
